import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Spin, Modal, Input, message, Tooltip } from 'antd';
import { CheckOutlined, CloseOutlined, EyeOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { Box, Text } from 'atoms';
import { Tabs } from 'atoms/Tabs';
import { AntdTable } from 'components/custom-table/Style';
import {
	getClientInvoiceRegenerationsV2,
	resolveClientInvoiceRegenerationV2,
} from 'store/clientInvoiceSliceV2';

const { TextArea } = Input;

const PageContainer = styled(Box)`
	max-width: 1600px;
	margin: 0 auto;
	padding: 24px;
`;
const PageTitle = styled(Text)`
	font-size: 24px;
	font-weight: 600;
	color: #333;
	display: block;
	margin-bottom: 4px;
`;
const PageSubtitle = styled(Text)`
	font-size: 14px;
	color: #707070;
	display: block;
	margin-bottom: 24px;
`;
const IconBtn = styled.span`
	cursor: pointer;
	font-size: 16px;
	margin-right: 14px;
`;

const formatCurrency = (value) => {
	if (value == null || value === '') return '-';
	const num = Number(value);
	return Number.isNaN(num) ? value : `₹ ${num.toLocaleString('en-IN')}`;
};
const formatDateTime = (v) => (v ? moment(v).format('DD-MM-YYYY HH:mm') : '-');

const TAB_ITEMS = [
	{ key: 'pending', label: 'Pending' },
	{ key: 'approved', label: 'Approved' },
	{ key: 'rejected', label: 'Rejected' },
];

function ClientInvoiceRegenApprovalPageV2() {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [activeTab, setActiveTab] = useState('pending');
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [rejectFor, setRejectFor] = useState(null);
	const [rejectReason, setRejectReason] = useState('');
	const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

	const fetchQueue = useCallback(() => {
		setLoading(true);
		dispatch(
			getClientInvoiceRegenerationsV2({
				filter_by: { approval_status: activeTab },
				pagination: { page: pagination.current, per_page: pagination.pageSize },
			})
		)
			.then((res) => {
				setRows(res?.rows || []);
				setPagination((prev) => ({ ...prev, total: res?.count || 0 }));
			})
			.finally(() => setLoading(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, activeTab, pagination.current, pagination.pageSize]);

	useEffect(() => {
		fetchQueue();
	}, [fetchQueue]);

	const openDetails = (record) =>
		navigate(`/approvals/client-invoice-edit-approval/${record.request_id}`, {
			state: { request: record },
		});

	const resolve = (request_id, decision, rejection_reason) => {
		setSaving(true);
		return dispatch(
			resolveClientInvoiceRegenerationV2({
				req_data: { request_id, decision, rejection_reason: rejection_reason || null },
				setErrors: () => {},
			})
		)
			.then((res) => {
				if (res?.status === 'success') fetchQueue();
				return res;
			})
			.catch(() => null)
			.finally(() => setSaving(false));
	};

	const handleApprove = (record) => {
		Modal.confirm({
			title: 'Approve charge edit?',
			content: `The edited charges on ${record.invoice_no || ''} will be applied.`,
			okText: 'Approve',
			onOk: () => resolve(record.request_id, 'approve'),
		});
	};

	const submitReject = () => {
		if (!rejectReason.trim()) {
			message.warning('Enter a rejection reason.');
			return;
		}
		resolve(rejectFor.request_id, 'reject', rejectReason.trim()).then(() => {
			setRejectFor(null);
			setRejectReason('');
		});
	};

	const baseColumns = [
		{
			title: 'Invoice No',
			dataIndex: 'invoice_no',
			key: 'invoice_no',
			render: (t) => t || '-',
		},
		{
			title: 'Changed',
			dataIndex: 'changed_fields',
			key: 'changed_fields',
			render: (t) => t || '-',
		},
		{ title: 'Project Name', dataIndex: 'project', key: 'project', render: (t) => t || '-' },
		{ title: 'Client', dataIndex: 'client', key: 'client', render: (t) => t || '-' },
		{
			title: 'Initial Invoice Value',
			dataIndex: 'initial_invoice_value',
			key: 'initial_invoice_value',
			render: (t) => formatCurrency(t),
		},
		{
			title: 'Updated Invoice Value',
			dataIndex: 'updated_invoice_value',
			key: 'updated_invoice_value',
			render: (t) => formatCurrency(t),
		},
		{
			title: 'Billing Period',
			dataIndex: 'billing_period',
			key: 'billing_period',
			render: (t) => t || '-',
		},
		{
			title: 'Created at',
			dataIndex: 'requested_at',
			key: 'requested_at',
			render: (t) => formatDateTime(t),
		},
	];

	const approvedCols =
		activeTab === 'approved'
			? [
					{
						title: 'Approved at',
						dataIndex: 'approved_at',
						key: 'approved_at',
						render: (t) => formatDateTime(t),
					},
					{
						title: 'Approved by',
						dataIndex: 'approved_by',
						key: 'approved_by',
						render: (t) => t || '-',
					},
			  ]
			: [];
	const rejectedCols =
		activeTab === 'rejected'
			? [
					{
						title: 'Rejected at',
						dataIndex: 'approved_at',
						key: 'rejected_at',
						render: (t) => formatDateTime(t),
					},
					{
						title: 'Rejected by',
						dataIndex: 'approved_by',
						key: 'rejected_by',
						render: (t) => t || '-',
					},
					{
						title: 'Reason',
						dataIndex: 'rejection_reason',
						key: 'rejection_reason',
						render: (t) => t || '-',
					},
			  ]
			: [];

	const actionCol = {
		title: 'Action',
		key: 'action',
		width: activeTab === 'pending' ? 150 : 70,
		render: (_, record) => (
			<div style={{ display: 'flex', alignItems: 'center' }}>
				<Tooltip title="View">
					<IconBtn>
						<EyeOutlined
							style={{ color: '#2563eb' }}
							onClick={() => openDetails(record)}
						/>
					</IconBtn>
				</Tooltip>
				{activeTab === 'pending' && (
					<>
						<Tooltip title="Approve">
							<IconBtn>
								<CheckOutlined
									style={{ color: '#52c41a' }}
									onClick={() => handleApprove(record)}
								/>
							</IconBtn>
						</Tooltip>
						<Tooltip title="Reject">
							<IconBtn>
								<CloseOutlined
									style={{ color: '#f44336' }}
									onClick={() => setRejectFor(record)}
								/>
							</IconBtn>
						</Tooltip>
					</>
				)}
			</div>
		),
	};

	const columns = [...baseColumns, ...approvedCols, ...rejectedCols, actionCol];

	return (
		<PageContainer>
			<PageTitle>Client Invoice Edit Approval</PageTitle>
			<PageSubtitle>Approve or reject client invoice regeneration requests</PageSubtitle>

			<Tabs
				activeKey={activeTab}
				onChange={(key) => {
					setActiveTab(key);
					setPagination((prev) => ({ ...prev, current: 1 }));
				}}
				items={TAB_ITEMS}
			/>

			<Spin spinning={loading}>
				<AntdTable
					rowKey="request_id"
					columns={columns}
					dataSource={rows}
					pagination={{
						current: pagination.current,
						pageSize: pagination.pageSize,
						total: pagination.total,
						showSizeChanger: true,
						pageSizeOptions: ['10', '20', '50'],
						showTotal: (t) => `Total ${t} requests`,
						onChange: (current, pageSize) =>
							setPagination((prev) => ({ ...prev, current, pageSize })),
					}}
				/>
			</Spin>

			<Modal
				title="Reject Regeneration"
				open={!!rejectFor}
				onCancel={() => setRejectFor(null)}
				onOk={submitReject}
				okText="Reject"
				okButtonProps={{ danger: true, loading: saving }}
			>
				<Text style={{ display: 'block', marginBottom: 8 }}>
					Rejection reason for invoice {rejectFor?.invoice_no || ''}
				</Text>
				<TextArea
					rows={3}
					value={rejectReason}
					onChange={(e) => setRejectReason(e.target.value)}
					placeholder="Why is this regeneration being rejected?"
				/>
			</Modal>
		</PageContainer>
	);
}

export default ClientInvoiceRegenApprovalPageV2;
